import React, { useState, useMemo } from 'react';
import { expenseToIncomeRatioExplanation } from '../locales/en-US.json';
import { Expense } from '../types/Expense';
import { useIntl, FormattedMessage } from 'react-intl';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    Button,
    Avatar,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    useTheme,
    Chip
} from '@mui/material';
import {
    DateRange,
    CheckCircle,
    ArrowForward,
    PieChart as PieChartIcon,
    AttachMoney
} from '@mui/icons-material';
import { designTokens } from '../theme/designTokens';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { addExpense } from '../firebase/firestoreOperations'; // Assuming this exists and is named like this
import { getAuth } from 'firebase/auth';

interface SharedExpensesProps {
    expenses: Expense[];
    payers: string[];
    payerColors: { [key: string]: string };
    onUpdateExpense: (expense: Expense) => Promise<void>;
    onAddExpense: (expense: Omit<Expense, 'id' | 'userId'>) => Promise<void>; // Need this to add reimbursement
}

const SharedExpenses: React.FC<SharedExpensesProps> = ({ expenses, payers, payerColors, onUpdateExpense, onAddExpense }) => {
    const intl = useIntl();
    const theme = useTheme();
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedPayers, setSelectedPayers] = useState<string[]>([]);
    const [settleUpModalOpen, setSettleUpModalOpen] = useState(false);
    const [settlementData, setSettlementData] = useState<{ from: string; to: string; amount: number } | null>(null);

    const months = Array.from({ length: 12 }, (_, i) => i);
    const years = Array.from(new Set(expenses.map(e => new Date(e.date).getFullYear()))).sort((a, b) => b - a);
    if (!years.includes(new Date().getFullYear())) {
        years.unshift(new Date().getFullYear());
    }

    // Initialize selectedPayers to all payers if empty
    React.useEffect(() => {
        if (selectedPayers.length === 0 && payers.length > 0) {
            setSelectedPayers(payers);
        }
    }, [payers]);

    // Filter relevant expenses
    const filteredExpenses = useMemo(() => {
        return expenses.filter(expense => {
            const expenseDate = new Date(expense.date);
            const isCorrectMonthYear = expenseDate.getMonth() === selectedMonth &&
                expenseDate.getFullYear() === selectedYear;

            if (!isCorrectMonthYear) return false;

            // Filter by selected payers
            // expense.payer must be in selectedPayers
            // AND if shared, sharedWith must be in selectedPayers
            // AND if debt, debtFrom must be in selectedPayers
            const isPayerSelected = selectedPayers.includes(expense.payer);
            const isSharedWithSelected = !expense.sharedWith || selectedPayers.includes(expense.sharedWith);
            const isDebtFromSelected = !expense.debtFrom || selectedPayers.includes(expense.debtFrom);

            return (expense.shared || expense.isDebt) && isPayerSelected && isSharedWithSelected && isDebtFromSelected;
        });
    }, [expenses, selectedMonth, selectedYear, selectedPayers]);

    // Calculate Debts
    const debtSummary = useMemo(() => {
        const debts: Record<string, Record<string, number>> = {};

        // Only initialize for selected payers
        const activePayers = payers.filter(p => selectedPayers.includes(p));

        activePayers.forEach(payer => {
            debts[payer] = {};
            activePayers.forEach(otherPayer => {
                if (payer !== otherPayer) {
                    debts[payer][otherPayer] = 0;
                }
            });
        });

        filteredExpenses.forEach(expense => {
            // Only process if both parties are in selectedPayers 
            // (filteredExpenses already filters for this, but safety check)

            if (expense.shared && expense.sharedWith && activePayers.includes(expense.payer) && activePayers.includes(expense.sharedWith)) {
                const halfAmount = expense.amount / 2;
                debts[expense.sharedWith][expense.payer] += halfAmount;
                debts[expense.payer][expense.sharedWith] -= halfAmount;
            }
            if (expense.isDebt && expense.debtFrom && activePayers.includes(expense.payer) && activePayers.includes(expense.debtFrom)) {
                debts[expense.payer][expense.debtFrom] += expense.amount;
            }
        });

        return Object.entries(debts)
            .flatMap(([payer, owed]) =>
                Object.entries(owed)
                    .filter(([_, amount]) => amount > 0.01) // Filter out negligible amounts
                    .map(([owedTo, amount]) => ({
                        from: payer,
                        to: owedTo,
                        amount: amount
                    }))
            )
            .sort((a, b) => b.amount - a.amount);
    }, [filteredExpenses, payers, selectedPayers]);

    // Category Distribution for Chart
    const categoryDistribution = useMemo(() => {
        const distribution: Record<string, number> = {};
        filteredExpenses.forEach(expense => {
            if ((expense.shared || expense.isDebt) && !expense.description.toLowerCase().includes('reembolso') && expense.category !== 'Reembolso') {
                // Exclude settlements from chart to show actual spending
                distribution[expense.category] = (distribution[expense.category] || 0) + expense.amount;
            }
        });
        return Object.entries(distribution)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [filteredExpenses]);

    const handleSettleUpClick = (debt: { from: string; to: string; amount: number }) => {
        setSettlementData(debt);
        setSettleUpModalOpen(true);
    };

    const confirmSettleUp = async () => {
        if (!settlementData) return;

        const description = `Reembolso: ${settlementData.from} -> ${settlementData.to}`;

        // Determine category (try to find a 'Transfer' or 'Reimbursement' category, or default to 'Other')
        // Ideally this should be dynamic, but for now defaulting to 'Transferências' or 'Outros' if simple.
        // We'll use 'Reembolso' as category if it doesn't exist it will be created or mapped to existing.

        try {
            await onAddExpense({
                description: description,
                amount: settlementData.amount,
                date: new Date().toISOString(),
                category: 'Reembolso',
                payer: settlementData.from,
                shared: false,
                isDebt: true,
                debtFrom: settlementData.to
            });
        } catch (e) {
            console.error(e);
        }
        setSettleUpModalOpen(false);
        setSettlementData(null);
    };



    const COLORS = [
        designTokens.colors.primary.main,
        designTokens.colors.success.main,
        designTokens.colors.warning.main,
        designTokens.colors.error.main,
        designTokens.colors.info.main,
        '#8884d8', '#82ca9d', '#ffc658'
    ];

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, height: '100%', overflow: 'auto' }}>
            {/* Header with Filters */}
            <Paper
                sx={{
                    p: 3,
                    borderRadius: designTokens.borderRadius.lg,
                    background: designTokens.colors.background.paper,
                    border: designTokens.glassmorphism.border,
                    boxShadow: designTokens.shadows.md,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel><FormattedMessage id="month" /></InputLabel>
                        <Select
                            value={selectedMonth}
                            label={intl.formatMessage({ id: 'month' })}
                            onChange={(e) => setSelectedMonth(Number(e.target.value))}
                        >
                            {months.map((month) => (
                                <MenuItem key={month} value={month}>
                                    {intl.formatMessage({ id: `month.${month}` })}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel><FormattedMessage id="year" /></InputLabel>
                        <Select
                            value={selectedYear}
                            label={intl.formatMessage({ id: 'year' })}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                        >
                            {years.map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel><FormattedMessage id="selectPayers" defaultMessage="Select People" /></InputLabel>
                        <Select
                            multiple
                            value={selectedPayers}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSelectedPayers(typeof value === 'string' ? value.split(',') : value);
                            }}
                            label={intl.formatMessage({ id: 'selectPayers', defaultMessage: 'Select People' })}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {payers.map((payer) => (
                                <MenuItem key={payer} value={payer}>
                                    {payer}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {payerColors && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {selectedPayers.map(payer => (
                                <Chip
                                    key={payer}
                                    label={payer}
                                    sx={{
                                        bgcolor: `${payerColors[payer]}22`,
                                        color: payerColors[payer],
                                        fontWeight: 'bold',
                                        border: `1px solid ${payerColors[payer]}44`
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Paper>

            <Grid container spacing={3}>
                {/* Visual Debts */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        <FormattedMessage id="whoOwesWho" defaultMessage="Who Owes Who" />
                    </Typography>
                    {debtSummary.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {debtSummary.map((debt: { from: string; to: string; amount: number }, index: number) => (
                                <Card
                                    key={index}
                                    sx={{
                                        background: designTokens.colors.background.paper,
                                        borderRadius: designTokens.borderRadius.lg,
                                        border: designTokens.glassmorphism.border,
                                        boxShadow: designTokens.shadows.sm,
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: designTokens.shadows.md
                                        }
                                    }}
                                >
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '16px !important' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: payerColors[debt.from] || 'grey' }}>{debt.from.charAt(0)}</Avatar>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary"><FormattedMessage id="debtor" /></Typography>
                                                <Typography variant="body2" fontWeight="bold">{debt.from}</Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <ArrowForward color="action" />
                                            <Typography variant="h6" color="error" fontWeight="bold">
                                                €{debt.amount.toFixed(2)}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary"><FormattedMessage id="creditor" /></Typography>
                                                <Typography variant="body2" fontWeight="bold">{debt.to}</Typography>
                                            </Box>
                                            <Avatar sx={{ bgcolor: payerColors[debt.to] || 'grey' }}>{debt.to.charAt(0)}</Avatar>
                                        </Box>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="success"
                                            onClick={() => handleSettleUpClick(debt)}
                                            startIcon={<CheckCircle />}
                                            sx={{ ml: 2, borderRadius: designTokens.borderRadius.full }}
                                        >
                                            <FormattedMessage id="settleUp" defaultMessage="Settle Up" />
                                        </Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    ) : (
                        <Paper
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                borderRadius: designTokens.borderRadius.lg,
                                bgcolor: `${designTokens.colors.success.main}11`,
                                color: designTokens.colors.success.main
                            }}
                        >
                            <CheckCircle sx={{ fontSize: 48, mb: 1, opacity: 0.8 }} />
                            <Typography variant="h6">
                                <FormattedMessage id="noSharedExpenses" defaultMessage="All settled up!" />
                            </Typography>
                        </Paper>
                    )}
                </Grid>

                {/* Distribution Chart */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        <FormattedMessage id="sharedExpensesDistribution" defaultMessage="Shared Expenses Distribution" />
                    </Typography>
                    <Paper
                        sx={{
                            p: 2,
                            height: 350,
                            borderRadius: designTokens.borderRadius.lg,
                            background: designTokens.colors.background.paper,
                            border: designTokens.glassmorphism.border,
                            boxShadow: designTokens.shadows.md
                        }}
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={categoryDistribution}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {categoryDistribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip
                                    formatter={(value: number) => [`€${value.toFixed(2)}`, intl.formatMessage({ id: 'amount' })]}
                                    contentStyle={{
                                        backgroundColor: designTokens.colors.background.paper,
                                        borderRadius: designTokens.borderRadius.md,
                                        border: designTokens.glassmorphism.border
                                    }}
                                />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Settle Up Modal */}
            <Dialog
                open={settleUpModalOpen}
                onClose={() => setSettleUpModalOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: designTokens.borderRadius.lg,
                        bgcolor: designTokens.colors.background.paper,
                        backgroundImage: 'none'
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}` }}>
                    <FormattedMessage id="settleUpModal.title" defaultMessage="Confirm Settlement" />
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="body1" gutterBottom>
                        <FormattedMessage
                            id="settleUpModal.description"
                            defaultMessage="Are you sure you want to record this settlement?"
                        />
                    </Typography>
                    {settlementData && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: designTokens.colors.background.elevated, borderRadius: designTokens.borderRadius.md }}>
                            <Typography variant="h6" align="center" sx={{ color: designTokens.colors.success.main, fontWeight: 'bold' }}>
                                €{settlementData.amount.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" align="center" color="text.secondary">
                                {settlementData.from} ➔ {settlementData.to}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${designTokens.colors.background.elevated}` }}>
                    <Button onClick={() => setSettleUpModalOpen(false)} color="inherit">
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button
                        onClick={confirmSettleUp}
                        variant="contained"
                        color="success"
                        autoFocus
                        startIcon={<CheckCircle />}
                    >
                        <FormattedMessage id="confirm" defaultMessage="Confirm" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SharedExpenses;
